import { Type } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isEqual } from 'lodash-es';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  from,
  isObservable,
  Observable,
  of,
  Subject,
  Subscription,
  zip,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { IPage, PagedCollection } from 'src/app/state';
import { catchAndContinue } from './catch-and-continue';
import { createOrEditFromParams } from './create-or-edit-from-params';
import { IAddParams, IEditParams, mapToAddEditParams, routeParamWithPrefix } from './route-helper';
import {
  convertFiltersToFilterGroup,
  convertNzFiltersToFilterGroup,
  IFilterGroup,
  ODataAndOr,
  ODataFilter,
} from './map-odata-filters';
import { ISort } from './sort';
import {
  ITableQueryParams,
  parseFiltersParam,
  parsePositiveIntParam,
  parseSortParam,
  stringifyFiltersParam,
  stringifySortParam,
  tableQueryParamNames,
} from './table-query-params';

interface IOptions<T, TDrawer extends NonNullable<unknown>> {
  route: ActivatedRoute;
  router: Router;
  paginator: PagedCollection<T>;

  /** Fetches one page. Errors are the caller's to report. */
  getPage(params: ITableQueryParams<T>): Observable<IPage<T>>;

  /** Prefixes the URL params, so two tables can share a page. */
  prefix: string;

  defaults?: { page: number; size: number };

  /**
   * Emits a filter that is applied to every request. When given, **no request is made
   * until it emits** — development-novelty-table needs the design id first.
   */
  staticFilters$?: Observable<ODataFilter>;

  /** Opens an add/edit drawer from `?<prefix>mode=add|edit`. Omit for a read-only table. */
  drawer?: {
    componentType: Type<TDrawer>;
    drawerService: NzDrawerService;
    notification: NzNotificationService;

    /** Human readable name, used in the drawer title. */
    name: string;
    getSingle(id: string): Observable<T>;
    extraParams?(): Observable<Partial<TDrawer>> | Partial<TDrawer> | undefined;
  };
}

/**
 * Keeps an `nz-table` in sync with the URL: page/size/sort/filters live in query params,
 * so a table is deep-linkable and survives a reload.
 *
 * Replaces `AbstractComponent`, which bundled this with pagination, drawer routing,
 * delete/patch and error display into a 601-LOC base class generic over four type
 * parameters — for two subclasses that each used a different subset. This is the one
 * genuinely shared part, so it is composed rather than inherited. See STEP-4D-PLAN.md.
 */
export class UrlPagedTable<T, TDrawer extends NonNullable<unknown> = NonNullable<unknown>> {
  private readonly params: ReturnType<typeof tableQueryParamNames>;
  private readonly navigateQueue$ = new Subject<ITableQueryParams<T>>();

  // Seeded, so `page$` fetches once without waiting for a refresh.
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  private readonly defaults: { page: number; size: number };
  private loadedRows: readonly T[] = [];

  /** Mirrors the current params for `[nzPageIndex]` / `[nzPageSize]` bindings. */
  public pageIndex: number;
  public perPage: number;
  public sort?: ISort<T>;
  public filters?: IFilterGroup;

  public readonly queryParams$: Observable<ITableQueryParams<T>>;
  public readonly page$: Observable<IPage<T>>;
  public readonly loading$: Observable<boolean>;

  /** `routeParam.add` / `.edit(id)` / `.clear`, for drawer links in the template. */
  public readonly routeParam: ReturnType<typeof routeParamWithPrefix>;

  public constructor(private readonly options: IOptions<T, TDrawer>) {
    this.params = tableQueryParamNames(options.prefix);
    this.defaults = options.defaults ?? { page: 1, size: 10 };
    this.pageIndex = this.defaults.page;
    this.perPage = this.defaults.size;
    this.routeParam = routeParamWithPrefix(options.prefix);

    this.loading$ = options.paginator.loading$;
    this.queryParams$ = this.buildQueryParams();
    this.page$ = this.buildPage();
  }

  /** Call from `ngOnInit`; the returned subscription belongs to the component. */
  public connect(): Subscription {
    const subscriptions = new Subscription();

    subscriptions.add(this.navigateQueue$.pipe(switchMap((p) => this.navigate(p))).subscribe());

    const drawer = this.options.drawer;

    if (drawer) {
      subscriptions.add(
        this.options.route.queryParams
          .pipe(
            map((params) => mapToAddEditParams(params, this.options.prefix)),
            // Other params share the URL; without this, changing one reopens the drawer.
            distinctUntilChanged<IAddParams | IEditParams | undefined>(
              (previous, current) => current?.mode === previous?.mode
            ),
            filter((params): params is IAddParams | IEditParams => !!params),
            switchMap((params) => {
              const extraParams = drawer.extraParams?.();

              return zip(of(params), isObservable(extraParams) ? extraParams : of(extraParams));
            }),
            createOrEditFromParams<TDrawer, T, Partial<TDrawer> | undefined>((extraParams) => ({
              componentType: drawer.componentType,
              drawerService: drawer.drawerService,
              extraParams,
              getSingle: (id) => drawer.getSingle(id),
              loadedEntities: this.loadedRows,
              name: drawer.name,
              notification: drawer.notification,
            }))
          )
          .subscribe((submitSuccess) => {
            this.closeDrawer(!!submitSuccess, drawer.name, drawer.notification);
          })
      );
    }

    subscriptions.add(
      this.queryParams$
        // pairwise skips the first emission: `page$` already fetches it, and setPage
        // there would queue a second request for the page just loaded.
        .pipe(pairwise())
        .subscribe(([previous, next]) => {
          // Only size/sort/filter changes invalidate; a page change is a cache lookup.
          if (
            previous.size !== next.size ||
            previous.sort !== next.sort ||
            previous.filters !== next.filters
          ) {
            this.options.paginator.clearCache();
          }

          this.options.paginator.setPage(next.page);
        })
    );

    // Recover from a page that no longer exists, e.g. after a filter narrows the results.
    subscriptions.add(
      this.page$
        .pipe(
          switchMap((page) => {
            if (page.currentPage <= page.lastPage || page.lastPage === 0) {
              return EMPTY;
            }

            console.warn(
              `The page ${page.currentPage}/${page.lastPage} is out of bounds. Falling back to page ${page.lastPage}.`
            );

            return from(this.navigateToPage(page.lastPage));
          })
        )
        .subscribe()
    );

    return subscriptions;
  }

  public onTableChange({ pageIndex, pageSize, filter, sort }: NzTableQueryParams): void {
    this.navigateQueue$.next({
      // TODO only supports one filter per property
      filters: convertNzFiltersToFilterGroup(filter),
      page: pageIndex,
      size: pageSize,
      // TODO only supports a single sort
      sort: sort
        .filter(({ value }) => value)
        .reduce<ISort<T> | undefined>(
          (accumulated, { key, value }) =>
            accumulated ?? { direction: value === 'ascend' ? 'asc' : 'desc', key: key as keyof T },
          undefined
        ),
    });
  }

  /** Refetches the current page. Clears the cache, or the refetch would be served from it. */
  public refresh(): void {
    this.options.paginator.clearCache();
    this.refresh$.next();
  }

  private buildQueryParams(): Observable<ITableQueryParams<T>> {
    const { route, staticFilters$ } = this.options;

    const distinctParams$ = route.queryParamMap.pipe(
      // Other params share the URL; only these four should retrigger a fetch.
      distinctUntilChanged((previous, next) =>
        Object.values(this.params).every((name) => previous.get(name) === next.get(name))
      )
    );

    return combineLatest([distinctParams$, staticFilters$ ?? of({})]).pipe(
      map(([urlParams, staticFilters]): ITableQueryParams<T> => {
        const sortParam = urlParams.get(this.params.sort);
        const filterParam = urlParams.get(this.params.filters);

        const filters: IFilterGroup = { andOr: ODataAndOr.And, filters: [] };
        const parsedFilters = parseFiltersParam(filterParam ?? undefined);

        if (parsedFilters) {
          filters.filters.push(parsedFilters);
        }

        filters.filters.push(convertFiltersToFilterGroup(staticFilters));

        return {
          filters,
          page: parsePositiveIntParam(urlParams.get(this.params.page) ?? '', this.defaults.page),
          size: parsePositiveIntParam(urlParams.get(this.params.size) ?? '', this.defaults.size),
          // TODO nzTable cannot be told its sort/filter state programmatically:
          // https://github.com/NG-ZORRO/ng-zorro-antd/issues/5304
          sort: parseSortParam<T>(sortParam ?? undefined),
        };
      }),
      tap((params) => {
        this.pageIndex = params.page;
        this.perPage = params.size;
        this.sort = params.sort ?? this.sort;
        this.filters = params.filters ?? this.filters;
      }),
      // Compares by value so `?` -> `?page=1` does not refetch: the parsed params are
      // identical once defaults are applied.
      distinctUntilChanged(isEqual),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private buildPage(): Observable<IPage<T>> {
    return combineLatest([this.refresh$, this.queryParams$]).pipe(
      switchMap(([, params]) => {
        // The URL is the source of truth for which page to show, so align the paginator
        // before reading its cache. Without this a deep link to ?<prefix>_page=3 would
        // serve page 1: the paginator starts there, and `connect`'s pairwise
        // deliberately skips the first setPage.
        this.options.paginator.setPage(params.page);

        // catchAndContinue, as AbstractComponent had: page$ drives every fetch, so an
        // error escaping here would terminate the stream and leave the table permanently
        // stuck. The service reports the failure on its own error$; this only has to keep
        // the stream alive. EMPTY also lets PagedCollection's finalize clear the spinner.
        return this.options.paginator.getPage(() => catchAndContinue(this.options.getPage(params)));
      }),
      // Remembered so the edit drawer can resolve a row without a request, which is what
      // reading Akita's paginator-written entity store used to achieve.
      tap(({ data }) => {
        this.loadedRows = data;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /** Returns to the table's URL, dropping the drawer's add/edit params. */
  private closeDrawer(
    submitSuccess: boolean,
    name: string,
    notification: NzNotificationService
  ): void {
    this.options.router
      .navigate([], {
        queryParams: this.routeParam.clear,
        queryParamsHandling: 'merge',
        // TODO leaves a duplicate history entry: /foo -> /foo/edit/1 -> /foo. Using
        // skipLocationChange would break deep links into the drawer.
        replaceUrl: true,
      })
      .catch(() => {
        notification.error('An error occurred', `Failed to update URL after ${name} drawer closed`);
      });

    if (submitSuccess) {
      this.refresh();
    }
  }

  private navigate(params: ITableQueryParams<T>): Promise<boolean> {
    return this.options.router.navigate([], {
      queryParams: {
        [this.params.filters]: params.filters ? stringifyFiltersParam(params.filters) : undefined,
        [this.params.page]: params.page,
        [this.params.size]: params.size,
        [this.params.sort]: params.sort ? stringifySortParam(params.sort) : undefined,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private navigateToPage(page: number): Promise<boolean> {
    return this.options.router.navigate([], {
      queryParams: { [this.params.page]: page },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
